import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { HiGift } from "react-icons/hi";
import { toast } from "react-toastify";
import { ModalShell } from "../atoms/ModalShell";
import { CustomButton } from "../atoms/CustomButton";
import { useActivateTrial } from "../../hooks/promoCode/trial";

interface TrialActivationModalProps {
  show: boolean;
  onClose: () => void;
  onActivated?: () => void;
}

export const TrialActivationModal: React.FC<TrialActivationModalProps> = ({
  show,
  onClose,
  onActivated,
}) => {
  const { t } = useTranslation();
  const { activate, isActivating, error } = useActivateTrial();
  const [attempted, setAttempted] = useState(false);

  const handleClose = () => {
    if (isActivating) return;
    setAttempted(false);
    onClose();
  };

  const handleConfirm = async () => {
    setAttempted(true);
    const ok = await activate();
    if (ok) {
      toast.success(t("dashboard.trialOffer.success"));
      setAttempted(false);
      onActivated?.();
      onClose();
    }
  };

  return (
    <ModalShell
      show={show}
      onClose={handleClose}
      size="md"
      footer={
        <>
          <CustomButton
            onClick={handleClose}
            disabled={isActivating}
            title={t("common.cancel") as string}
          />
          <CustomButton
            variant="primary"
            onClick={handleConfirm}
            disabled={isActivating}
            isLoading={isActivating}
            title={t("dashboard.trialOffer.confirm") as string}
          />
        </>
      }
    >
      <div className="flex flex-col gap-3 items-center">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <HiGift size={24} className="text-blue-600" />
        </div>
        <div className="text-gray-900 text-lg font-semibold font-figtree leading-7 text-center">
          {t("dashboard.trialOffer.modalTitle")}
        </div>
        <ul className="w-full mt-1 flex flex-col gap-2">
          {["point1", "point2"].map((key) => (
            <li
              key={key}
              className="flex items-start gap-2 text-sm text-gray-600 font-figtree"
            >
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
              {t(`dashboard.trialOffer.${key}`)}
            </li>
          ))}
        </ul>
        {attempted && error && (
          <p className="mt-1 text-sm text-red-600 text-center">{error}</p>
        )}
      </div>
    </ModalShell>
  );
};

export default TrialActivationModal;
